import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { generatePath, Link, useSearchParams } from 'react-router-dom';
import {
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, ChevronDown, LoaderCircle, Pencil, PlusIcon, TrashIcon, XIcon } from 'lucide-react';

import { assigneeOptionValue, findTeamMemberByAssigneeId, useGetTeamMembers } from '@/api/settings/useGetTeamMembers';
import { useGetTicketActivity, type TicketActivityRow } from '@/api/tickets/useGetTicketActivity';
import { useAddComment } from '@/api/tickets/useAddComment';
import { useCreateColumn } from '@/api/tickets/useCreateColumn';
import { useCreateTicket } from '@/api/tickets/useCreateTicket';
import { useDeleteColumn } from '@/api/tickets/useDeleteColumn';
import { useDeleteTicket } from '@/api/tickets/useDeleteTicket';
import { useGetComments } from '@/api/tickets/useGetComments';
import { Column as ApiColumn, useGetTickets } from '@/api/tickets/useGetTickets';
import { useReorderColumns } from '@/api/tickets/useReorderColumns';
import { useUpdateColumn } from '@/api/tickets/useUpdateColumn';
import { useUpdateTicket } from '@/api/tickets/useUpdateTicket';
import { useVoteTicket } from '@/api/tickets/useVoteTicket';
import { useGetAudienceByWorkspace } from '@/api/workspaces/useGetAudienceByWorkspace';
import { brandColors } from '@/app/constants';
import Avatar from '@/component-library/primitives/Avatar/Avatar';
import { Button } from '@/component-library/primitives/Button/Button';
import TicketsLoader from '@/component-library/primitives/Loaders/TicketsLoader';
import { AlertDialog } from '@/component-library/widgets/AlertDialog/AlertDialog';
import { ConfirmDialog } from '@/component-library/widgets/ConfirmDialog/ConfirmDialog';
import { usePageTracking } from '@/hooks/useTracking';
import { Member } from '@/models/AudienceByWorkspace';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { AppRoutes } from '@/utils/routes/router';
import { getSignalIdFromTicketMetadata } from '@/utils/ticketSignalMetadata';

import { Ticket as ServiceTicket } from '../../../services/ticketService';

interface Comment {
  commentId: string;
  ticketId: string;
  content: string;
  isInternal: boolean;
  /** API returns ISO strings; some paths may use `Date`. */
  createdAt: Date | string;
  updatedAt: Date | string;
  createdBy: string;
  createdByName: string;
  updatedBy?: string;
  updatedByName?: string;
}

interface Ticket {
  ticketId: string;
  title: string;
  description: string;
  /** API may send numeric enums (System.Text.Json without string enums). */
  priority: 'low' | 'medium' | 'high' | 'urgent' | number;
  status: 'open' | 'in_progress' | 'resolved' | 'closed' | number;
  type: 'bug' | 'feature_request' | 'support' | 'churn_signal' | 'general';
  assigneeId?: string;
  assigneeName?: string;
  assigneeEmail?: string;
  tags: string[];
  dueDate?: string;
  estimatedHours?: number; // TODO: Not displayed in UI - for future time tracking feature
  actualHours?: number; // TODO: Not displayed in UI - for future time tracking feature
  upvotes?: string[];
  downvotes?: string[];
  voteCount?: number;
  commentCount?: number; // Number of comments for this ticket
  churnSignalId?: string;
  churnSignalData?: {
    signalType: string;
    riskScore: number;
    userEmail: string;
    userName: string;
  };
  columnId?: string;
  /** Raw API tickets use ISO strings; column processing may use `Date`. */
  createdAt: Date | string;
  updatedAt: Date | string;
  createdBy: string;
  createdByName: string;
  updatedBy?: string;
  updatedByName?: string;
  /** Omitted on list responses until loaded; column processing uses `[]` when missing. */
  comments?: Comment[];
  audienceLinks?: Array<{
    id?: string;
    audienceMemberId: string;
    audienceMemberEmail?: string | null;
    audienceMemberName?: string | null;
  }>;
  archived?: boolean;
  /** From API JSONB — e.g. `{ signalId }` for tickets created from a churn signal */
  metadata?: Record<string, unknown>;
  id?: string;
}

interface Column {
  id: string;
  title: string;
  tickets: Ticket[];
}

// No more hardcoded columns - only use columns from backend API

// Helper function to get priority colors
const getPriorityColor = (priority: string | number) => {
  const key = typeof priority === 'number' ? String(priority) : priority.toLowerCase();
  switch (key) {
    case 'high':
    case 'urgent':
      return 'bg-custom-red/10 text-custom-red border-custom-red/20';
    case 'medium':
      return 'bg-custom-yellow/10 text-custom-yellow border-custom-yellow/20';
    case 'low':
      return 'bg-custom-green/10 text-custom-green border-custom-green/20';
    default:
      return 'bg-custom-blue/10 text-custom-blue border-custom-blue/20';
  }
};

// Helper function to get status colors for ticket cards
const getStatusColor = (status: string) => {
  switch (status) {
    case 'open':
      return 'bg-gray-100 text-gray-700';
    case 'in_progress':
      return 'bg-amber-100 text-amber-700';
    case 'resolved':
      return 'bg-green-100 text-green-700';
    case 'closed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

// Helper function to get status labels
const getStatusLabel = (status: string) => {
  switch (status) {
    case 'open':
      return 'Open';
    case 'in_progress':
      return 'In Progress';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
    default:
      return 'Open';
  }
};

/** Calendar day for `<input type="date" />` without UTC/local off-by-one from ISO timestamps. */
function dueDateToDateInputValue(dueDate: string | undefined): string {
  if (!dueDate) return '';
  const trimmed = dueDate.trim();
  const ymd = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
  if (ymd) return ymd[1];
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeAudienceLinks(raw: unknown): Ticket['audienceLinks'] {
  if (!Array.isArray(raw)) return undefined;
  const mapped = raw
    .map((item: any) => ({
      id: item.id,
      audienceMemberId: String(item.audienceMemberId ?? item.AudienceMemberId ?? ''),
      audienceMemberEmail: item.audienceMemberEmail ?? item.AudienceMemberEmail ?? null,
      audienceMemberName: item.audienceMemberName ?? item.AudienceMemberName ?? null,
    }))
    .filter((x) => x.audienceMemberId.length > 0);
  return mapped.length > 0 ? mapped : undefined;
}

function audienceLinkLabel(link: {
  audienceMemberName?: string | null;
  audienceMemberEmail?: string | null;
  audienceMemberId: string;
}) {
  const n = link.audienceMemberName?.trim();
  const e = link.audienceMemberEmail?.trim();
  if (n) return n;
  if (e) return e;
  return link.audienceMemberId;
}

type AudienceMemberListPickerProps = {
  members: Member[];
  query: string;
  onQueryChange: (q: string) => void;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelectMember: (memberId: string) => void;
  containerRef: React.Ref<HTMLDivElement>;
  collapsedLabel: string;
  compact?: boolean;
};

/** Click the row to open; type in the field to narrow the list; pick a row to add. */
function AudienceMemberListPicker({
  members,
  query,
  onQueryChange,
  isOpen,
  onOpen,
  onClose,
  onSelectMember,
  containerRef,
  collapsedLabel,
  compact,
}: AudienceMemberListPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const textSize = compact ? 'text-xs' : 'text-sm';
  const pad = compact ? 'px-2 py-1.5' : 'px-3 py-2';

  return (
    <div ref={containerRef} className="relative z-20 w-full min-w-0 max-w-full">
      {!isOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className={`w-full flex items-center justify-between gap-2 ${pad} border border-custom-mediumSilver rounded-lg bg-white text-left text-custom-darkSilver hover:bg-custom-silver/40 ${textSize}`}
        >
          <span className="truncate">{collapsedLabel}</span>
          <ChevronDown className="w-4 h-4 shrink-0 opacity-60" aria-hidden />
        </button>
      ) : (
        <div
          className={`w-full min-w-0 max-w-full border border-custom-mediumSilver rounded-lg bg-white shadow-lg overflow-hidden ${textSize}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            className={`flex items-center gap-1 border-b border-custom-mediumSilver ${
              compact ? 'px-1 py-0.5' : 'px-2 py-1'
            }`}
          >
            <input
              ref={inputRef}
              type="search"
              autoComplete="off"
              placeholder="Type to filter…"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              className={`flex-1 min-w-0 ${pad} border-0 rounded focus:ring-0 focus:outline-none bg-transparent text-custom-black placeholder:text-custom-darkSilver`}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  onClose();
                }
              }}
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-custom-darkSilver hover:text-custom-black shrink-0 rounded"
              aria-label="Close list"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <ul className="max-h-48 overflow-y-auto py-1">
            {members.length === 0 ? (
              <li className={`${pad} text-custom-darkSilver`}>No matching members</li>
            ) : (
              members.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`w-full text-left ${pad} hover:bg-custom-silver/80 ${textSize}`}
                    onClick={() => onSelectMember(m.id)}
                  >
                    <span className="font-medium text-custom-black">{m.name || '—'}</span>
                    {m.email ? <span className="block text-custom-darkSilver truncate">{m.email}</span> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const TicketCard: React.FC<{
  ticket: Ticket;
  onDelete: (ticketId: string) => void;
  onClick: (ticket: Ticket) => void;
  teamMembers: any[];
}> = ({ ticket, onDelete, onClick, teamMembers }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ticket.ticketId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getTeamMemberById = (id: string) => findTeamMemberByAssigneeId(teamMembers, id);

  const getDisplayName = (member: any) => {
    if (member.userName && member.userName !== member.email) {
      return member.userName;
    }
    // Extract name from email (everything before @)
    return member.email ? member.email.split('@')[0] : 'Unknown';
  };

  const assignee = ticket.assigneeId ? getTeamMemberById(ticket.assigneeId) : null;

  // Calculate vote count and comment count
  const voteCount = (ticket.upvotes?.length || 0) - (ticket.downvotes?.length || 0);
  const commentCount = ticket.commentCount || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        e.stopPropagation();
        onClick(ticket);
      }}
      className={`bg-white rounded-lg border border-custom-mediumSilver p-3 cursor-pointer hover:shadow-sm transition-shadow ${
        isDragging ? 'opacity-50' : ''
      } ${ticket.archived ? 'opacity-70 border-dashed' : ''}`}
    >
      {/* Priority at top */}
      <div className="flex justify-between items-start mb-2">
        <span className={`px-2 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
          {ticket.priority}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(ticket.ticketId);
          }}
          className="text-custom-darkSilver hover:text-custom-red transition-colors"
        >
          <TrashIcon size={14} />
        </button>
      </div>

      {/* Title */}
      <h3 className="font-semibold text-black text-sm mb-2 leading-tight">{ticket.title}</h3>

      {/* Description */}
      <p
        className="text-custom-darkSilver text-xs mb-3 overflow-hidden"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.3',
          maxHeight: '2.6em',
        }}
      >
        {ticket.description}
      </p>

      {/* Assignee */}
      {assignee && (
        <div className="flex items-center gap-1 mb-3">
          <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                getDisplayName(assignee),
              )}&background=6B7280&color=fff&size=16`}
              alt={getDisplayName(assignee)}
              className="w-full h-full object-cover"
            />
          </div>
          <span className="text-xs text-gray-500">{getDisplayName(assignee)}</span>
        </div>
      )}

      {/* Status */}
      {/* <div className="mb-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
          {getStatusLabel(ticket.status)}
        </span>
      </div> */}

      {/* Footer with vote and comment counts */}
      <div className="flex items-center justify-between pt-2 border-t border-custom-mediumSilver">
        <div className="flex items-center gap-3">
          {/* Vote count */}
          {/* <div className="flex items-center gap-1">
            <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-xs text-gray-500">{voteCount}</span>
          </div> */}

          {/* Comment count */}
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3 text-custom-darkSilver" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-xs text-custom-darkSilver">{commentCount}</span>
          </div>

          {(ticket.audienceLinks?.length ?? 0) > 0 && (
            <span className="text-xs text-custom-darkSilver" title="Related audience members">
              {(ticket.audienceLinks ?? []).length} member
              {(ticket.audienceLinks ?? []).length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {/* Due date if exists */}
        {ticket.dueDate && (
          <div className="text-xs text-custom-darkSilver">Due: {new Date(ticket.dueDate).toLocaleDateString()}</div>
        )}
      </div>
    </div>
  );
};

const Column: React.FC<{
  column: Column;
  onDeleteTicket: (ticketId: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onStartEditing: (columnId: string, currentTitle: string) => void;
  isEditing: boolean;
  editingTitle: string;
  onTitleChange: (title: string) => void;
  onSaveEdit: (columnId: string) => void;
  onCancelEdit: () => void;
  onAddTicket: (columnId: string) => void;
  onTicketClick: (ticket: Ticket) => void;
  teamMembers: any[];
  isSaving?: boolean;
}> = ({
  column,
  onDeleteTicket,
  onDeleteColumn,
  onStartEditing,
  isEditing,
  editingTitle,
  onTitleChange,
  onSaveEdit,
  onCancelEdit,
  onAddTicket,
  onTicketClick,
  teamMembers,
  isSaving = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle click outside to cancel editing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && inputRef.current && !inputRef.current.contains(event.target as Node)) {
        onCancelEdit();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, onCancelEdit]);

  return (
    <div
      ref={(node) => {
        setSortableRef(node);
        setNodeRef(node);
      }}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-custom-silver rounded-lg p-4 min-h-[300px] w-64 transition-colors flex flex-col shrink-0 space-y-4 ${
        isOver ? '!bg-custom-mediumSilver' : ''
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between">
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              ref={inputRef}
              type="text"
              value={editingTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              className="flex-1 px-2 py-1 text-sm font-semibold text-custom-darkSilver border border-custom-darkSilver rounded outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSaveEdit(column.id);
                } else if (e.key === 'Escape') {
                  onCancelEdit();
                }
              }}
            />
            <button
              onClick={() => onSaveEdit(column.id)}
              disabled={isSaving}
              className="text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[24px]"
              title="Save"
            >
              {isSaving ? <LoaderCircle className="animate-spin h-4 w-4" /> : '✓'}
            </button>
            <button onClick={onCancelEdit} className="text-red-600 hover:text-red-800 transition-colors" title="Cancel">
              ✕
            </button>
          </div>
        ) : (
          <>
            <h2
              className="font-semibold text-custom-black flex-1 cursor-pointer hover:bg-custom-mediumSilver px-2 py-1 mr-2 rounded transition-colors"
              onClick={() => onStartEditing(column.id, column.title)}
            >
              {column.title}
            </h2>
            <div className="flex items-center gap-2">
              <span className="bg-custom-mediumSilver text-custom-darkSilver text-xs w-[24px] h-[24px] rounded-full text-center leading-[24px]">
                {column.tickets.length}
              </span>
              <button
                onClick={() => onDeleteColumn(column.id)}
                className="text-custom-darkSilver hover:text-custom-red transition-colors"
                title="Delete column"
              >
                <XIcon size={14} />
              </button>
            </div>
          </>
        )}
      </div>

      <SortableContext items={column.tickets.map((ticket) => ticket.ticketId)} strategy={verticalListSortingStrategy}>
        {column.tickets.length === 0 ? (
          // Empty column - center the add button
          <div className="flex items-center justify-center min-h-[calc(100%-52px)]">
            <button
              onClick={() => onAddTicket(column.id)}
              className="p-2 text-custom-darkSilver hover:text-custom-black text-sm transition-colors flex items-center justify-center gap-1"
            >
              <PlusIcon size={20} />
              <span className="text-sm font-medium">Add new ticket</span>
            </button>
          </div>
        ) : (
          // Column with tickets - show tickets and add button at bottom
          <>
            <div className="space-y-2">
              {column.tickets.map((ticket) => (
                <TicketCard
                  key={ticket.ticketId}
                  ticket={ticket}
                  onDelete={onDeleteTicket}
                  onClick={onTicketClick}
                  teamMembers={teamMembers}
                />
              ))}
            </div>
            {/* Add new ticket button at bottom */}
            <button
              onClick={() => onAddTicket(column.id)}
              className="mt-auto text-custom-darkSilver hover:text-custom-black text-sm transition-colors flex items-center justify-center gap-1"
            >
              <PlusIcon size={20} />
              <span className="text-sm font-medium">Add new ticket</span>
            </button>
          </>
        )}
      </SortableContext>
    </div>
  );
};

function formatTicketActivityActor(row: Pick<TicketActivityRow, 'createdBy' | 'createdByName' | 'createdByEmail'>): string {
  const name = row.createdByName?.trim();
  const email = row.createdByEmail?.trim();
  if (name && email) return `${name} (${email})`;
  if (email) return email;
  if (name) return name;
  return row.createdBy;
}

const Tickets: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: teamMembers = [] } = useGetTeamMembers();
  const { data: audienceWorkspace } = useGetAudienceByWorkspace({ mode: 'all' });
  const audienceMembers = useMemo(() => {
    const raw = audienceWorkspace?.members ?? audienceWorkspace?.audience ?? [];
    return Array.isArray(raw) ? (raw as Member[]) : [];
  }, [audienceWorkspace]);
  const { user } = useAuth();

  // Track page view
  usePageTracking('tickets');

  const [showArchived, setShowArchived] = useState(false);

  // API hooks
  const { data: apiData, isLoading: ticketsLoading, error: ticketsError } = useGetTickets({
    includeArchived: showArchived,
  });
  const createTicketMutation = useCreateTicket();
  const createColumnMutation = useCreateColumn();
  const updateTicketMutation = useUpdateTicket();
  const updateColumnMutation = useUpdateColumn();
  const deleteTicketMutation = useDeleteTicket();
  const deleteColumnMutation = useDeleteColumn();
  const reorderColumnsMutation = useReorderColumns();
  const voteTicketMutation = useVoteTicket();
  const addCommentMutation = useAddComment();

  // Extract tickets and columns from API response
  const tickets = apiData?.tickets || [];
  const apiColumns = apiData?.columns || [];

  const meAsAssignee = useMemo(() => {
    const email = (user?.attributes?.email || '').trim().toLowerCase();
    if (!email) return '';
    const m = teamMembers.find((x) => (x.email || '').toLowerCase() === email);
    return m ? assigneeOptionValue(m) : '';
  }, [teamMembers, user?.attributes?.email]);

  const dueSoonAssignedCount = useMemo(() => {
    if (!meAsAssignee) return 0;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const endMs = start.getTime() + 7 * 86400000;
    return tickets.filter((t: { archived?: boolean; assigneeId?: string; dueDate?: string }) => {
      if (t.archived) return false;
      const aid = String(t.assigneeId || '');
      if (aid !== meAsAssignee && aid !== (user?.attributes?.email || '')) return false;
      if (!t.dueDate) return false;
      const d = new Date(t.dueDate).getTime();
      return d >= start.getTime() && d <= endMs;
    }).length;
  }, [tickets, meAsAssignee, user?.attributes?.email]);

  console.log('📊 API Data:', {
    ticketCount: tickets.length,
    columnCount: apiColumns.length,
    columns: apiColumns,
    tickets: tickets,
  });

  // Convert API columns to local format with proper field mapping
  const convertApiColumnsToLocal = useCallback((apiColumns: any[]): Column[] => {
    const converted = apiColumns
      .sort((a, b) => (a.position || 0) - (b.position || 0)) // Sort by position
      .map((column) => ({
        id: column.id,
        title: column.name, // Map 'name' to 'title'
        tickets: [], // Initialize empty tickets array - will be populated by matching tickets
        ticketOrder: column.ticketOrder || [], // Store the ticket order array from backend
      }));

    console.log('🔄 Converted API columns to local:', converted);
    return converted;
  }, []);

  // Convert API tickets to local format and assign to columns
  const convertTicketsToColumns = useCallback((tickets: any[], columns: Column[]): Column[] => {
    // Process tickets to handle nested structure and ensure proper ticketId
    const processedTickets = tickets.map((ticket) => {
      // Handle nested Item structure from DynamoDB
      const ticketData = ticket.Item || ticket;

      // Extract ticket ID from SK field (format: "Ticket#uuid") or fallback to other fields
      const extractTicketId = (data: any): string => {
        // Try SK field first (format: "Ticket#uuid")
        if (data.SK && data.SK.startsWith('Ticket#')) {
          return data.SK.replace('Ticket#', '');
        }
        // Fallback to other possible fields
        return data.id || data.ticketId || `temp-${Math.random().toString(36).substr(2, 9)}`;
      };

      const ticketId = extractTicketId(ticketData);

      return {
        ...ticketData,
        ticketId,
        // Ensure dates are properly formatted
        createdAt: new Date(ticketData.createdAt || new Date().toISOString()),
        updatedAt: new Date(ticketData.lastModifiedAt || ticketData.updatedAt || new Date().toISOString()),
        // Ensure comments array exists
        comments: ticketData.comments || [],
        // API may omit commentCount; derive it from comments when needed.
        commentCount:
          typeof ticketData.commentCount === 'number'
            ? ticketData.commentCount
            : Array.isArray(ticketData.comments)
            ? ticketData.comments.length
            : 0,
        audienceLinks: normalizeAudienceLinks(ticketData.audienceLinks),
        archived: Boolean(ticketData.archived),
      };
    });

    // Create a map of ticketId -> ticket for quick lookup
    const ticketMap = new Map(processedTickets.map((ticket) => [ticket.ticketId, ticket]));

    // Map columns with their tickets in the order specified by ticketOrder array
    return columns.map((column: any) => {
      let orderedTickets: any[] = [];

      if (column.ticketOrder && column.ticketOrder.length > 0) {
        // Use the column's ticketOrder array to determine display order
        orderedTickets = column.ticketOrder
          .map((ticketId: string) => ticketMap.get(ticketId))
          .filter((ticket: any) => ticket !== undefined); // Filter out tickets that don't exist
      } else {
        // Fallback: get all tickets for this column (no specific order)
        orderedTickets = processedTickets.filter((ticket) => ticket.columnId === column.id);
      }

      return {
        ...column,
        tickets: orderedTickets,
      };
    });
  }, []);

  // Compute columns using useMemo to prevent unnecessary re-renders
  const columns = useMemo(() => {
    console.log('🧮 Computing columns...', {
      apiColumnsLength: apiColumns.length,
      ticketsLength: tickets.length,
    });

    if (apiColumns.length > 0 && tickets.length >= 0) {
      const baseColumns = convertApiColumnsToLocal(apiColumns);
      const columnsWithTickets = convertTicketsToColumns(tickets, baseColumns);
      console.log('✅ Columns computed (with tickets):', columnsWithTickets);
      return columnsWithTickets;
    } else if (apiColumns.length > 0) {
      // Only columns, no tickets
      const result = convertApiColumnsToLocal(apiColumns);
      console.log('✅ Columns computed (no tickets):', result);
      return result;
    } else {
      console.log('❌ No columns found');
      return []; // No fallback to hardcoded columns
    }
  }, [apiColumns, tickets, convertApiColumnsToLocal, convertTicketsToColumns]);

  const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  // Fetch comments for the selected ticket
  const { data: commentsData, isLoading: commentsLoading } = useGetComments(selectedTicket?.ticketId || null);
  const { data: activityRows = [], isLoading: activityLoading } = useGetTicketActivity(selectedTicket?.ticketId || null);

  const [editingTicketTitle, setEditingTicketTitle] = useState(false);
  const [editingTicketDescription, setEditingTicketDescription] = useState(false);
  const [tempTicketTitle, setTempTicketTitle] = useState('');
  const [tempTicketDescription, setTempTicketDescription] = useState('');
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [targetColumnForNewTicket, setTargetColumnForNewTicket] = useState<string | null>(null);
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [isAddingLabel, setIsAddingLabel] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    assigneeId: '',
    relatedMemberIds: [] as string[],
  });
  const [memberPickerQuery, setMemberPickerQuery] = useState('');
  const [addAudiencePickerOpen, setAddAudiencePickerOpen] = useState(false);
  const [detailAudiencePickerOpen, setDetailAudiencePickerOpen] = useState(false);
  const addAudiencePickerRef = useRef<HTMLDivElement>(null);
  const detailAudiencePickerRef = useRef<HTMLDivElement>(null);
  const [newTicketErrors, setNewTicketErrors] = useState({
    title: false,
    description: false,
  });
  const [newColumn, setNewColumn] = useState({
    title: '',
  });
  const [editingColumnTitle, setEditingColumnTitle] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const assigneeOverlayRef = useRef<HTMLDivElement>(null);
  const [showAssigneeSelector, setShowAssigneeSelector] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [newComment, setNewComment] = useState('');

  const openAssigneeSelector = useCallback(() => {
    setDetailAudiencePickerOpen(false);
    setAddAudiencePickerOpen(false);
    setMemberPickerQuery('');
    setShowAssigneeSelector(true);
  }, []);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {
      // Default no-op function
    },
  });

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
  });

  // Helper function to get display name
  const getDisplayName = (member: any) => {
    if (member.userName && member.userName !== member.email) {
      return member.userName;
    }
    // Extract name from email (everything before @)
    return member.email ? member.email.split('@')[0] : 'Unknown';
  };

  // Predefined label colors (rotates through colors for visual variety)
  const tagColors = [
    'bg-blue-100 text-blue-800 border-blue-200',
    'bg-purple-100 text-purple-800 border-purple-200',
    'bg-pink-100 text-pink-800 border-pink-200',
    'bg-orange-100 text-orange-800 border-orange-200',
    'bg-teal-100 text-teal-800 border-teal-200',
    'bg-indigo-100 text-indigo-800 border-indigo-200',
  ];

  // Get color for a tag based on its text (consistent coloring via hash)
  const getTagColor = (tag: string) => {
    const hash = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return tagColors[hash % tagColors.length];
  };

  // Filter team members based on search term
  const filteredTeamMembers = teamMembers.filter((member) =>
    getDisplayName(member).toLowerCase().includes(assigneeSearchTerm.toLowerCase()),
  );

  const linksFromMemberIds = useCallback(
    (ids: string[]): Ticket['audienceLinks'] => {
      if (ids.length === 0) return undefined;
      return ids.map((audienceMemberId) => {
        const m = audienceMembers.find((x) => x.id === audienceMemberId);
        return {
          audienceMemberId,
          audienceMemberEmail: m?.email ?? null,
          audienceMemberName: m?.name ?? null,
        };
      });
    },
    [audienceMembers],
  );

  const persistTicketAudienceMembers = async (ticket: Ticket, nextIds: string[]) => {
    await updateTicketMutation.mutateAsync({
      ticketId: ticket.ticketId,
      data: { audienceMemberIds: nextIds },
    });
    setSelectedTicket({
      ...ticket,
      audienceLinks: linksFromMemberIds(nextIds),
    });
  };

  const availableForNewTicket = useMemo(() => {
    const sel = new Set(newTicket.relatedMemberIds);
    const q = memberPickerQuery.trim().toLowerCase();
    return audienceMembers.filter((m) => {
      if (sel.has(m.id)) return false;
      if (!q) return true;
      const name = (m.name || '').toLowerCase();
      const email = (m.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [audienceMembers, newTicket.relatedMemberIds, memberPickerQuery]);

  const availableForDetailTicket = useMemo(() => {
    const cur = new Set((selectedTicket?.audienceLinks ?? []).map((l) => String(l.audienceMemberId)));
    const q = memberPickerQuery.trim().toLowerCase();
    return audienceMembers.filter((m) => {
      if (cur.has(m.id)) return false;
      if (!q) return true;
      const name = (m.name || '').toLowerCase();
      const email = (m.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [audienceMembers, selectedTicket?.audienceLinks, memberPickerQuery]);

  useEffect(() => {
    if (!addAudiencePickerOpen && !detailAudiencePickerOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (addAudiencePickerOpen && addAudiencePickerRef.current && !addAudiencePickerRef.current.contains(t)) {
        setAddAudiencePickerOpen(false);
      }
      if (detailAudiencePickerOpen && detailAudiencePickerRef.current && !detailAudiencePickerRef.current.contains(t)) {
        setDetailAudiencePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addAudiencePickerOpen, detailAudiencePickerOpen]);

  // Handle click outside modal to close (ignore clicks on portaled assignee overlay — it is not under modalRef)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showTicketModal || !modalRef.current) return;
      const t = event.target as Node;
      if (modalRef.current.contains(t)) return;
      if (assigneeOverlayRef.current?.contains(t)) return;
      handleCloseTicketModal();
    };

    if (showTicketModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTicketModal]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const ticket = findTicketById(active.id as string);
    console.log(`🎯 Drag started for ticket: ${active.id}`, ticket);
    setActiveTicket(ticket);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeTicket = findTicketById(activeId);
    if (!activeTicket) return;

    const activeColumn = findColumnByTicketId(activeId);
    const overColumn = findColumnByTicketId(overId) || findColumnById(overId);

    if (!activeColumn || !overColumn) return;

    if (activeColumn.id === overColumn.id) return;

    // Note: Removed local state update since columns is now computed with useMemo
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    console.log(`🎯 Drag ended: ${active.id} → ${over?.id}`);

    if (!over) {
      console.log('❌ No drop target found');
      setActiveTicket(null);
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    // Check if we're dragging a column
    const activeColumn = findColumnById(activeId);
    const overColumn = findColumnById(overId);

    if (activeColumn && overColumn) {
      // Calculate new positions for all columns
      const columnIds = columns.map((col) => col.id);
      const activeIndex = columnIds.indexOf(activeId);
      const overIndex = columnIds.indexOf(overId);

      if (activeIndex === -1 || overIndex === -1) {
        console.log('❌ Column not found in columns array');
        return;
      }

      // Create reordered column IDs array
      const reorderedIds = Array.from(columnIds);
      const [removed] = reorderedIds.splice(activeIndex, 1);
      reorderedIds.splice(overIndex, 0, removed);

      // Create position updates for all columns
      const columnUpdates = reorderedIds.map((columnId, index) => ({
        columnId,
        position: index,
      }));

      console.log('🔄 Reordering columns:', {
        activeColumn: activeColumn.title,
        overColumn: overColumn.title,
        activeIndex,
        overIndex,
        updates: columnUpdates,
      });

      // Call reorder API
      reorderColumnsMutation.mutate({ columns: columnUpdates });
      // Clear active ticket immediately for columns (no visual issue since columns don't have ghost effect)
      setActiveTicket(null);
      return;
    }

    // Handle ticket dragging
    const activeTicketColumn = findColumnByTicketId(activeId);
    const overTicketColumn = findColumnByTicketId(overId) || findColumnById(overId);

    if (!activeTicketColumn || !overTicketColumn) return;

    if (activeTicketColumn.id === overTicketColumn.id) {
      // Reorder within the same column
      const activeTicket = findTicketById(activeId);
      if (!activeTicket) return;

      // Find array indices (NOT database position values)
      const oldIndex = activeTicketColumn.tickets.findIndex((t) => t.ticketId === activeId);
      let newIndex: number;

      if (overId === activeTicketColumn.id) {
        // Dropped on column (empty space) - move to end
        newIndex = activeTicketColumn.tickets.length - 1;
      } else {
        // Dropped on another ticket - find its array index
        newIndex = activeTicketColumn.tickets.findIndex((t) => t.ticketId === overId);
      }

      // If indices are the same, no need to update
      if (oldIndex === newIndex) {
        console.log('🔄 Ticket already at target position, skipping update');
        return;
      }

      console.log(
        `🔄 Reordering ticket ${activeId} from index ${oldIndex} to index ${newIndex} in column ${activeTicketColumn.id}`,
      );

      // Update position in backend using explicit from/to API
      updateTicketMutation.mutate(
        {
          ticketId: activeId,
          data: {
            fromColumnId: activeTicketColumn.id,
            toColumnId: activeTicketColumn.id,
            fromPosition: oldIndex,
            toPosition: newIndex,
          },
        },
        {
          onSettled: () => {
            // Clear active ticket after mutation completes (success or error)
            // This prevents the ghost/snap-back effect
            setActiveTicket(null);
          },
        },
      );
    } else {
      // Move ticket to different column
      const activeTicket = findTicketById(activeId);
      if (!activeTicket) return;

      // Find the correct position in the new column (array index)
      let newPosition: number;

      if (overId === overTicketColumn.id) {
        // Dropped on column (empty space) - move to end
        newPosition = overTicketColumn.tickets.length;
      } else {
        // Dropped on another ticket - find its array index
        const targetIndex = overTicketColumn.tickets.findIndex((t) => t.ticketId === overId);
        newPosition = targetIndex !== -1 ? targetIndex : overTicketColumn.tickets.length;
      }

      // Get current position of the ticket in its current column
      const currentPosition = activeTicketColumn.tickets.findIndex((t) => t.ticketId === activeId);

      console.log(
        `🔄 Moving ticket ${activeId} from column ${activeTicketColumn.id}[${currentPosition}] to column ${overTicketColumn.id}[${newPosition}]`,
      );

      // Board order + ColumnId are updated server-side (do not send string status — API enums differ from web labels)
      updateTicketMutation.mutate(
        {
          ticketId: activeId,
          data: {
            fromColumnId: activeTicketColumn.id,
            toColumnId: overTicketColumn.id,
            fromPosition: currentPosition,
            toPosition: newPosition,
          },
        },
        {
          onSettled: () => {
            // Clear active ticket after mutation completes (success or error)
            // This prevents the ghost/snap-back effect
            setActiveTicket(null);
          },
        },
      );
    }
  };

  const findTicketById = (ticketId: string): Ticket | null => {
    for (const column of columns) {
      const ticket = column.tickets.find((t) => t.ticketId === ticketId);
      if (ticket) return ticket;
    }
    return null;
  };

  const findColumnByTicketId = (ticketId: string): Column | null => {
    return columns.find((column) => column.tickets.some((ticket) => ticket.ticketId === ticketId)) || null;
  };

  const findColumnById = (columnId: string): Column | null => {
    return columns.find((column) => column.id === columnId) || null;
  };

  const handleAddTicket = async () => {
    // Prevent multiple submissions
    if (createTicketMutation.isLoading) {
      return;
    }

    // Validate required fields
    const errors = {
      title: !newTicket.title.trim(),
      description: !newTicket.description.trim(),
    };

    setNewTicketErrors(errors);

    // If there are any validation errors, don't submit
    if (errors.title || errors.description) {
      return;
    }

    try {
      // Use the target column ID from state, or fallback to first column
      const columnId = targetColumnForNewTicket || (apiColumns.length > 0 ? apiColumns[0].id : 'todo');

      console.log('🎫 Creating ticket in column:', columnId);

      await createTicketMutation.mutateAsync({
        title: newTicket.title,
        description: newTicket.description,
        type: 'general',
        priority: newTicket.priority,
        assigneeId: newTicket.assigneeId || undefined,
        tags: [],
        // position will be auto-assigned by backend based on existing tickets in column
        columnId: columnId,
        audienceMemberIds: newTicket.relatedMemberIds.length > 0 ? newTicket.relatedMemberIds : undefined,
      });

      setNewTicket({ title: '', description: '', priority: 'medium', assigneeId: '', relatedMemberIds: [] });
      setMemberPickerQuery('');
      setAddAudiencePickerOpen(false);
      setNewTicketErrors({ title: false, description: false });
      setShowAddModal(false);
      setTargetColumnForNewTicket(null); // Clear target column
      // Reset mutation state so user can create another ticket
      createTicketMutation.reset();
    } catch (error) {
      console.error('Failed to create ticket:', error);
      // Error handling is done in the mutation
    }
  };

  const handleDeleteTicket = (ticketId: string) => {
    // Prevent opening dialog if deletion is in progress
    if (deleteTicketMutation.isLoading || deleteColumnMutation.isLoading) {
      return;
    }

    const ticket = tickets.find((t) => t.ticketId === ticketId);
    const ticketTitle = ticket?.title || 'this ticket';

    // Close any existing dialog first
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Ticket',
      message: `Are you sure you want to delete "${ticketTitle}"?\n\nThis action cannot be undone.`,
      onConfirm: async () => {
        // Prevent multiple submissions - check current state, not closure
        if (deleteTicketMutation.isLoading) {
          return;
        }

        // Close dialog using functional update to get latest state
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          await deleteTicketMutation.mutateAsync(ticketId);
          // Close the ticket details modal if it's open
          if (selectedTicket?.ticketId === ticketId) {
            setShowTicketModal(false);
            setSelectedTicket(null);
          }
        } catch (error: any) {
          console.error('Failed to delete ticket:', error);

          // Show error message from backend
          const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to delete ticket. Please try again.';
          setAlertDialog({
            isOpen: true,
            title: 'Delete Failed',
            message: errorMessage,
          });
        }
      },
    });
  };

  const handleClearAllTickets = async () => {
    if (window.confirm('Are you sure you want to clear all tickets? This action cannot be undone.')) {
      // Delete all tickets one by one
      try {
        const deletePromises = tickets.map((ticket) => deleteTicketMutation.mutateAsync(ticket.ticketId));
        await Promise.all(deletePromises);
      } catch (error) {
        console.error('Failed to clear all tickets:', error);
      }
    }
  };

  const handleAddColumn = async () => {
    // Prevent multiple submissions
    if (createColumnMutation.isLoading) {
      return;
    }

    if (!newColumn.title.trim()) return;

    try {
      await createColumnMutation.mutateAsync({
        name: newColumn.title,
        description: '',
        color: '#6B7280', // Default gray color
        position: apiColumns.length, // Add at the end
      });

      setNewColumn({ title: '' });
      setShowAddColumnModal(false);
      // Reset mutation state so user can create another column
      createColumnMutation.reset();
    } catch (error) {
      console.error('Failed to create column:', error);
      // Error handling is done in the mutation
    }
  };

  const handleRenameColumn = async (columnId: string) => {
    // Prevent multiple submissions
    if (updateColumnMutation.isLoading) {
      return;
    }

    if (!editingColumnTitle.trim()) return;

    try {
      console.log('🏷️ Renaming column:', columnId, 'to:', editingColumnTitle);

      await updateColumnMutation.mutateAsync({
        columnId,
        data: {
          name: editingColumnTitle,
        },
      });

      // Clear editing state
      setEditingColumn(null);
      setEditingColumnTitle('');
      // Reset mutation state so user can rename another column
      updateColumnMutation.reset();

      console.log('✅ Column renamed successfully');
    } catch (error) {
      console.error('❌ Failed to rename column:', error);
      // Error handling is done in the mutation
    }
  };

  const handleDeleteColumn = (columnId: string) => {
    const column = columns.find((c) => c.id === columnId);
    const columnName = column?.title || 'this column';
    const ticketCount = column?.tickets?.length || 0;

    // Close any open confirm dialog first
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    // Prevent deletion if it's the last column
    if (columns.length === 1) {
      setAlertDialog({
        isOpen: true,
        title: 'Cannot Delete Last Column',
        message: `"${columnName}" is the last remaining column and cannot be deleted.\n\nYou must have at least one column to organize your tickets.`,
      });
      return;
    }

    // If column has tickets, show info alert instead of confirm
    if (ticketCount > 0) {
      setAlertDialog({
        isOpen: true,
        title: 'Cannot Delete Column',
        message: `Column "${columnName}" contains ${ticketCount} ticket${
          ticketCount > 1 ? 's' : ''
        }.\n\nTo delete this column, please move all tickets to another column first.`,
      });
      return;
    }

    // Show confirmation dialog for empty columns (but not the last one)
    // Prevent opening dialog if deletion is in progress
    if (deleteTicketMutation.isLoading || deleteColumnMutation.isLoading) {
      return;
    }

    // Close any existing dialog first
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));

    setConfirmDialog({
      isOpen: true,
      title: 'Delete Column',
      message: `Are you sure you want to delete "${columnName}"?\n\nThis action cannot be undone.`,
      onConfirm: async () => {
        // Prevent multiple submissions - check current state, not closure
        if (deleteColumnMutation.isLoading) {
          return;
        }

        // Close dialog using functional update to get latest state
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          await deleteColumnMutation.mutateAsync(columnId);
        } catch (error: any) {
          console.error('Failed to delete column:', error);

          // Show error message from backend
          const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to delete column. Please try again.';
          setAlertDialog({
            isOpen: true,
            title: 'Delete Failed',
            message: errorMessage,
          });
        }
      },
    });
  };

  const startEditingColumn = (columnId: string, currentTitle: string) => {
    setEditingColumn(columnId);
    setEditingColumnTitle(currentTitle);
  };

  const cancelEditingColumn = () => {
    setEditingColumn(null);
    setEditingColumnTitle('');
  };

  const handleTicketClick = useCallback((ticket: Ticket) => {
    setMemberPickerQuery('');
    setAddAudiencePickerOpen(false);
    setDetailAudiencePickerOpen(false);
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  }, []);

  useEffect(() => {
    const openId = searchParams.get('openTicket');
    if (!openId || ticketsLoading) return;
    if (!tickets.length) return;
    const found = tickets.find((t) => String(t.ticketId) === openId || String(t.id) === openId);
    if (!found) return;
    handleTicketClick(found);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openTicket');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, tickets, ticketsLoading, handleTicketClick, setSearchParams]);

  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setSelectedTicket(null);
    setMemberPickerQuery('');
    setAddAudiencePickerOpen(false);
    setDetailAudiencePickerOpen(false);
    setShowAssigneeSelector(false);
    setAssigneeSearchTerm('');
    setEditingTicketTitle(false);
    setEditingTicketDescription(false);
    setNewComment(''); // Clear comment input
    // Reset mutation states when modal closes
    addCommentMutation.reset();
  };

  const handleSaveTicketTitle = async () => {
    if (!selectedTicket || !tempTicketTitle.trim()) return;

    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: {
          title: tempTicketTitle,
        },
      });

      // Update local state
      setSelectedTicket({ ...selectedTicket, title: tempTicketTitle });
      setEditingTicketTitle(false);
    } catch (error) {
      console.error('Failed to update ticket title:', error);
    }
  };

  const handleSaveTicketDescription = async () => {
    if (!selectedTicket) return;

    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: {
          description: tempTicketDescription,
        },
      });

      // Update local state
      setSelectedTicket({ ...selectedTicket, description: tempTicketDescription });
      setEditingTicketDescription(false);
    } catch (error) {
      console.error('Failed to update ticket description:', error);
    }
  };

  const updateTicketAssignee = async (ticketId: string, assigneeId: string | undefined | null) => {
    try {
      const newAssignee = assigneeId ? findTeamMemberByAssigneeId(teamMembers, assigneeId) : null;

      // Update local state immediately for instant feedback
      if (selectedTicket && selectedTicket.ticketId === ticketId) {
        setSelectedTicket({
          ...selectedTicket,
          assigneeId: assigneeId || undefined,
          assigneeName: newAssignee ? getDisplayName(newAssignee) : undefined,
          assigneeEmail: newAssignee?.email || undefined,
        });
      }

      await updateTicketMutation.mutateAsync({
        ticketId,
        data: {
          updateAssignee: true,
          // Use null instead of undefined so it's not stripped by axios
          assigneeId: assigneeId || null,
          assigneeName: newAssignee ? getDisplayName(newAssignee) : null,
          assigneeEmail: newAssignee?.email || null,
        },
      });

      // Try to add a comment about the assignee change (non-blocking)
      const commentText = assigneeId
        ? `Assigned to ${newAssignee ? getDisplayName(newAssignee) : 'Unknown User'}`
        : 'Removed assignee';

      addCommentMutation
        .mutateAsync({
          ticketId,
          data: {
            content: commentText,
            isInternal: false,
          },
        })
        .catch((err) => {
          // Comment creation failed, but assignee update succeeded - just log it
          console.warn('Failed to add assignee change comment (non-critical):', err);
        });
    } catch (error) {
      console.error('Failed to update ticket assignee:', error);
      // Revert local state on error
      if (selectedTicket && selectedTicket.ticketId === ticketId) {
        // Refresh from cache or revert
        setSelectedTicket({ ...selectedTicket });
      }
    }
  };

  const handleAddComment = async () => {
    // Prevent multiple submissions
    if (addCommentMutation.isLoading || !newComment.trim() || !selectedTicket) {
      return;
    }

    try {
      await addCommentMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: {
          content: newComment.trim(),
          isInternal: false,
        },
      });

      setNewComment('');
      // Reset mutation state so user can add another comment
      addCommentMutation.reset();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleAddLabel = async () => {
    if (!newLabel.trim() || !selectedTicket || isAddingLabel) return;

    const tagToAdd = newLabel.trim();
    const previousTags = selectedTicket.tags || [];
    const updatedTags = [...previousTags, tagToAdd];

    try {
      setIsAddingLabel(true);

      // Update local state and hide input immediately (optimistic)
      setSelectedTicket({
        ...selectedTicket,
        tags: updatedTags,
      });
      setNewLabel('');
      setShowLabelInput(false);

      // Send to backend
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: {
          tags: updatedTags,
        },
      });
    } catch (error) {
      console.error('Failed to add label:', error);

      // Revert on error - restore previous tags and show input again
      setSelectedTicket({
        ...selectedTicket,
        tags: previousTags,
      });
      setNewLabel(tagToAdd); // Restore the tag text so user can try again
      setShowLabelInput(true);
    } finally {
      setIsAddingLabel(false);
    }
  };

  const handleRemoveLabel = async (labelToRemove: string) => {
    if (!selectedTicket) return;

    try {
      const updatedTags = (selectedTicket.tags || []).filter((tag) => tag !== labelToRemove);

      // Update local state immediately
      setSelectedTicket({
        ...selectedTicket,
        tags: updatedTags,
      });

      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: {
          tags: updatedTags,
        },
      });
    } catch (error) {
      console.error('Failed to remove label:', error);
      // Revert on error
      setSelectedTicket({
        ...selectedTicket,
        tags: selectedTicket.tags || [],
      });
    }
  };

  const handleVote = async (voteType: 'upvote' | 'downvote' | 'remove') => {
    if (!selectedTicket) return;

    try {
      const userId = user?.signInUserSession?.idToken.payload.sub || '';

      // Optimistically update local state
      const upvotes = [...(selectedTicket.upvotes || [])];
      const downvotes = [...(selectedTicket.downvotes || [])];

      // Remove user from both arrays
      const upvoteIndex = upvotes.indexOf(userId);
      const downvoteIndex = downvotes.indexOf(userId);
      if (upvoteIndex > -1) upvotes.splice(upvoteIndex, 1);
      if (downvoteIndex > -1) downvotes.splice(downvoteIndex, 1);

      // Add to appropriate array
      if (voteType === 'upvote') {
        upvotes.push(userId);
      } else if (voteType === 'downvote') {
        downvotes.push(userId);
      }

      const voteCount = upvotes.length - downvotes.length;

      // Update local state immediately
      setSelectedTicket({
        ...selectedTicket,
        upvotes,
        downvotes,
        voteCount,
      });

      await voteTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { voteType },
      });
    } catch (error) {
      console.error('Failed to vote:', error);
      // Revert on error (query cache will handle this via onError)
    }
  };

  const getUserVote = (ticket: Ticket): 'upvote' | 'downvote' | null => {
    const userId = user?.signInUserSession?.idToken.payload.sub;
    if (!userId) return null;
    if (ticket.upvotes?.includes(userId)) return 'upvote';
    if (ticket.downvotes?.includes(userId)) return 'downvote';
    return null;
  };

  const handleArchiveTicket = async () => {
    if (!selectedTicket) return;
    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { archived: true },
      });
      setSelectedTicket({ ...selectedTicket, archived: true });
    } catch (e) {
      console.error('Failed to archive ticket:', e);
    }
  };

  const handleRestoreTicket = async () => {
    if (!selectedTicket) return;
    try {
      await updateTicketMutation.mutateAsync({
        ticketId: selectedTicket.ticketId,
        data: { archived: false },
      });
      setSelectedTicket({ ...selectedTicket, archived: false });
    } catch (e) {
      console.error('Failed to restore ticket:', e);
    }
  };

  if (ticketsLoading) {
    return <TicketsLoader />;
  }

  if (ticketsError) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-custom-red mb-2">Failed to load tickets and columns</p>
          <p className="text-custom-darkSilver">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  // Show empty state when no columns are available from backend
  if (!ticketsLoading && columns.length === 0) {
    return (
      <>
        <div className="p-6 h-full flex items-center justify-center">
          <div className="text-center">
            <div className="mb-6">
              <button
                onClick={() => setShowAddColumnModal(true)}
                disabled={createColumnMutation.isLoading}
                className="w-16 h-16 mx-auto mb-4 bg-custom-blue hover:bg-custom-darkBlue disabled:bg-blue-400 rounded-full flex items-center justify-center transition-colors group disabled:cursor-not-allowed"
              >
                {createColumnMutation.isLoading ? (
                  <LoaderCircle size={32} className="text-white animate-spin" />
                ) : (
                  <PlusIcon size={32} className="text-white" />
                )}
              </button>
              <h3 className="text-lg font-semibold text-custom-black">No columns configured</h3>
              <p className="text-custom-darkSilver">Get started by creating your first column to organize tickets</p>
            </div>
          </div>
        </div>

        {/* Add Column Modal */}
        {showAddColumnModal &&
          createPortal(
            <div
              data-testid="add-column-modal"
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
              style={{ zIndex: 9999 }}
            >
              <div className="bg-white rounded-lg p-6 mx-4">
                <h2 className="text-xl font-semibold mb-4">Add New Column</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-custom-black mb-1">Enter the name of the column</label>
                    <input
                      type="text"
                      value={newColumn.title}
                      onChange={(e) => setNewColumn({ ...newColumn, title: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddColumn();
                        } else if (e.key === 'Escape') {
                          setShowAddColumnModal(false);
                        }
                      }}
                      className="min-w-[450px] px-3 py-2 border border-custom-darkSilver rounded-lg focus:outline-none focus:border-custom-blue"
                      placeholder="Column name"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-8">
                  <Button
                    variant="transparent"
                    disabled={createColumnMutation.isLoading}
                    onClick={() => setShowAddColumnModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddColumn}
                    disabled={createColumnMutation.isLoading}
                    isLoading={createColumnMutation.isLoading}
                  >
                    Add Column
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  }

  return (
    <div className="p-6 h-full bg-white">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {dueSoonAssignedCount > 0 ? (
          <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-xl">
            You have <strong>{dueSoonAssignedCount}</strong> assigned ticket{dueSoonAssignedCount === 1 ? '' : 's'}{' '}
            with a due date in the next 7 days.
          </div>
        ) : (
          <span />
        )}
        <label className="flex items-center gap-2 text-sm text-custom-black cursor-pointer shrink-0">
          <input
            type="checkbox"
            className="rounded border-custom-mediumSilver"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        autoScroll={{ threshold: { x: 0, y: 0.2 } }}
      >
        <div className="flex gap-6 overflow-x-auto pb-4">
          <SortableContext items={columns.map((col) => col.id)} strategy={horizontalListSortingStrategy}>
            {columns.map((column) => (
              <Column
                key={column.id}
                column={column}
                onDeleteTicket={handleDeleteTicket}
                onDeleteColumn={handleDeleteColumn}
                onStartEditing={startEditingColumn}
                isEditing={editingColumn === column.id}
                editingTitle={editingColumnTitle}
                onTitleChange={setEditingColumnTitle}
                onSaveEdit={(columnId) => {
                  if (!updateColumnMutation.isLoading) {
                    handleRenameColumn(columnId);
                  }
                }}
                isSaving={updateColumnMutation.isLoading}
                onCancelEdit={cancelEditingColumn}
                onAddTicket={(columnId: string) => {
                  setTargetColumnForNewTicket(columnId);
                  setMemberPickerQuery('');
                  setAddAudiencePickerOpen(false);
                  setDetailAudiencePickerOpen(false);
                  setShowAddModal(true);
                }}
                onTicketClick={handleTicketClick}
                teamMembers={teamMembers}
              />
            ))}
          </SortableContext>
          {/* Add Column Button - Trello Style */}
          <div className="bg-custom-silver rounded-lg min-h-[200px] w-20 flex-shrink-0">
            <button
              onClick={() => setShowAddColumnModal(true)}
              className="w-full h-full flex items-center justify-center text-custom-darkSilver hover:text-black hover:bg-custom-mediumSilver rounded-lg transition-colors"
            >
              <div className="text-center">
                <span className="text-sm font-medium [writing-mode:vertical-lr] rotate-180">Add another column</span>
                <PlusIcon size={16} className="mx-auto mb-2" />
              </div>
            </button>
          </div>
        </div>

        <DragOverlay
          dropAnimation={{
            duration: 0,
            easing: 'cubic-bezier(0, 0, 0, 1)',
          }}
        >
          {activeTicket ? (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-64 opacity-90">
              <h3 className="font-semibold text-gray-900 text-sm mb-2">{activeTicket.title}</h3>
              <p className="text-gray-600 text-xs">{activeTicket.description}</p>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Add Ticket Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-lg p-6 w-96 max-w-full mx-4"
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                handleAddTicket();
              } else if (e.key === 'Escape') {
                setShowAddModal(false);
                setMemberPickerQuery('');
                setAddAudiencePickerOpen(false);
                setNewTicketErrors({ title: false, description: false });
                setNewTicket((prev) => ({ ...prev, relatedMemberIds: [] }));
              }
            }}
          >
            <h2 className="text-xl font-semibold mb-4">
              Add New Ticket
              {targetColumnForNewTicket && columns.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  to {columns.find((c) => c.id === targetColumnForNewTicket)?.title || 'column'}
                </span>
              )}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTicket.title}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, title: e.target.value });
                    // Clear error when user starts typing
                    if (newTicketErrors.title) {
                      setNewTicketErrors({ ...newTicketErrors, title: false });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    newTicketErrors.title ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="Enter ticket title"
                />
                {newTicketErrors.title && <p className="text-red-500 text-sm mt-1">Title is required</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, description: e.target.value });
                    // Clear error when user starts typing
                    if (newTicketErrors.description) {
                      setNewTicketErrors({ ...newTicketErrors, description: false });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    newTicketErrors.description
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  rows={3}
                  placeholder="Enter ticket description"
                />
                {newTicketErrors.description && <p className="text-red-500 text-sm mt-1">Description is required</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={newTicket.priority}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, priority: e.target.value as 'low' | 'medium' | 'high' })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                <select
                  value={newTicket.assigneeId}
                  onChange={(e) => setNewTicket({ ...newTicket, assigneeId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select team member</option>
                  {teamMembers.map((member) => (
                    <option key={assigneeOptionValue(member)} value={assigneeOptionValue(member)}>
                      {member.userName && member.userName !== member.email
                        ? member.userName
                        : member.email.split('@')[0]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Related audience members</label>
                <p className="text-xs text-gray-500 mb-2">Optional — link to one or more customers.</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newTicket.relatedMemberIds.map((id) => {
                    const m = audienceMembers.find((x) => x.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 border border-gray-200"
                      >
                        <Link
                          to={generatePath(AppRoutes.memberProfile, { id })}
                          className="truncate max-w-[12rem] text-blue-700 hover:underline"
                          onClick={() => {
                            setShowAddModal(false);
                            setAddAudiencePickerOpen(false);
                          }}
                        >
                          {m?.name || m?.email || id}
                        </Link>
                        <button
                          type="button"
                          className="text-gray-500 hover:text-red-600 shrink-0"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setNewTicket({
                              ...newTicket,
                              relatedMemberIds: newTicket.relatedMemberIds.filter((x) => x !== id),
                            });
                          }}
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <AudienceMemberListPicker
                  members={availableForNewTicket}
                  query={memberPickerQuery}
                  onQueryChange={setMemberPickerQuery}
                  isOpen={addAudiencePickerOpen}
                  onOpen={() => {
                    setDetailAudiencePickerOpen(false);
                    setAddAudiencePickerOpen(true);
                    setMemberPickerQuery('');
                  }}
                  onClose={() => setAddAudiencePickerOpen(false)}
                  onSelectMember={(id) => {
                    if (newTicket.relatedMemberIds.includes(id)) return;
                    setNewTicket({
                      ...newTicket,
                      relatedMemberIds: [...newTicket.relatedMemberIds, id],
                    });
                    setMemberPickerQuery('');
                  }}
                  containerRef={addAudiencePickerRef}
                  collapsedLabel="Click to search and add members…"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setMemberPickerQuery('');
                  setAddAudiencePickerOpen(false);
                  setNewTicketErrors({ title: false, description: false });
                  // Reset mutation state when modal closes
                  createTicketMutation.reset();
                }}
                disabled={createTicketMutation.isLoading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTicket}
                autoFocus
                disabled={createTicketMutation.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center gap-2 min-w-[100px]"
              >
                {createTicketMutation.isLoading ? (
                  <>
                    <LoaderCircle className="animate-spin h-4 w-4" />
                    <span>Adding...</span>
                  </>
                ) : (
                  'Add Ticket'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Column Modal */}
      {showAddColumnModal &&
        createPortal(
          <div
            data-testid="add-column-modal"
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 9999 }}
          >
            <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
              <h2 className="text-xl font-semibold mb-4">Add New Column</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Column Name</label>
                  <input
                    type="text"
                    value={newColumn.title}
                    onChange={(e) => setNewColumn({ ...newColumn, title: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddColumn();
                      } else if (e.key === 'Escape') {
                        setShowAddColumnModal(false);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter column name"
                    autoFocus
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAddColumnModal(false)}
                  disabled={createColumnMutation.isLoading || createColumnMutation.isSuccess}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddColumn}
                  disabled={createColumnMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {createColumnMutation.isLoading && <LoaderCircle className="animate-spin h-4 w-4" />}
                  Add Column
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Ticket Detail Modal */}
      {showTicketModal && selectedTicket && (
        <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={modalRef} className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col min-h-0 min-w-0">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {editingTicketTitle ? (
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={tempTicketTitle}
                        onChange={(e) => setTempTicketTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveTicketTitle();
                          } else if (e.key === 'Escape') {
                            setEditingTicketTitle(false);
                          }
                        }}
                        className="flex-1 text-xl font-semibold text-gray-900 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />

                      <button type="button" onClick={handleSaveTicketTitle} className="group">
                        <span className="group-hover:[&>*_path]:stroke-custom-green">
                          <Check color={brandColors.darkSilver} size={16} />
                        </span>
                      </button>
                      <button type="button" onClick={() => setEditingTicketTitle(false)} className="group">
                        <span className="group-hover:[&>*_path]:stroke-custom-red">
                          <TrashIcon color={brandColors.darkSilver} size={16} />
                        </span>
                      </button>
                    </div>
                  ) : (
                    <h2
                      className="text-xl font-semibold text-gray-900 mb-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors inline-block"
                      onClick={() => {
                        setEditingTicketTitle(true);
                        setTempTicketTitle(selectedTicket.title);
                      }}
                      title="Click to edit"
                    >
                      {selectedTicket.title}
                    </h2>
                  )}
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedTicket.priority === 'high'
                          ? 'bg-red-100 text-red-800 border border-red-200'
                          : selectedTicket.priority === 'medium'
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          : 'bg-green-100 text-green-800 border border-green-200'
                      }`}
                    >
                      {selectedTicket.priority}
                    </span>
                    <span className="text-sm text-gray-500">
                      Created on {new Date(selectedTicket.createdAt).toLocaleDateString()} at{' '}
                      {new Date(selectedTicket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {selectedTicket.archived ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                        Archived
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedTicket.archived ? (
                      <Button variant="primary" size="x-small" onClick={handleRestoreTicket}>
                        Restore from archive
                      </Button>
                    ) : (
                      <Button variant="transparent" size="x-small" onClick={handleArchiveTicket}>
                        Archive
                      </Button>
                    )}
                  </div>
                  {(() => {
                    const linkedSignalId = getSignalIdFromTicketMetadata(selectedTicket.metadata);
                    if (!linkedSignalId) return null;
                    return (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-1">Churn signal</p>
                        <Link
                          to={`${AppRoutes.signals}?signalId=${encodeURIComponent(linkedSignalId)}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          onClick={() => setShowTicketModal(false)}
                        >
                          View linked signal
                        </Link>
                      </div>
                    );
                  })()}
                </div>
                <button
                  onClick={handleCloseTicketModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <XIcon size={20} />
                </button>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="flex h-[calc(90vh-140px)] min-h-0 min-w-0">
              {/* Left Panel - Ticket Details */}
              <div className="flex-1 min-w-0 p-6 border-r border-gray-200 overflow-y-auto overflow-x-hidden">
                {/* Description */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-medium text-custom-black">Description</h3>
                  </div>
                  {editingTicketDescription ? (
                    <div className="space-y-2">
                      <textarea
                        value={tempTicketDescription}
                        onChange={(e) => setTempTicketDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        rows={4}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button variant="primary" size="x-small" onClick={handleSaveTicketDescription}>
                          Save
                        </Button>
                        <Button variant="transparent" size="x-small" onClick={() => setEditingTicketDescription(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="bg-custom-silver rounded-lg p-4 cursor-pointer hover:bg-custom-silver/50 hover:shadow-sm transition-colors"
                      onClick={() => {
                        setEditingTicketDescription(true);
                        setTempTicketDescription(selectedTicket.description);
                      }}
                      title="Click to edit"
                    >
                      <p className="text-custom-black text-sm leading-relaxed whitespace-pre-wrap">
                        {selectedTicket.description || 'Click to add description...'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Related audience members (customers) */}
                <div className="mb-6 min-w-0 max-w-full">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Related audience members</h3>
                  <p className="text-xs text-custom-darkSilver mb-2">
                    Customers this ticket applies to — you can attach several to one ticket.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(selectedTicket.audienceLinks ?? []).map((link) => {
                      const id = String(link.audienceMemberId);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-custom-silver text-custom-black border border-custom-mediumSilver"
                        >
                          <Link
                            to={generatePath(AppRoutes.memberProfile, { id })}
                            className="truncate max-w-[12rem] text-custom-blue hover:underline"
                            onClick={() => setShowTicketModal(false)}
                          >
                            {audienceLinkLabel(link)}
                          </Link>
                          <button
                            type="button"
                            className="text-custom-darkSilver hover:text-custom-red shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              void persistTicketAudienceMembers(
                                selectedTicket,
                                (selectedTicket.audienceLinks ?? [])
                                  .map((l) => String(l.audienceMemberId))
                                  .filter((x) => x !== id),
                              );
                            }}
                            aria-label="Remove member"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <AudienceMemberListPicker
                    members={availableForDetailTicket}
                    query={memberPickerQuery}
                    onQueryChange={setMemberPickerQuery}
                    isOpen={detailAudiencePickerOpen}
                    onOpen={() => {
                      setAddAudiencePickerOpen(false);
                      setDetailAudiencePickerOpen(true);
                      setMemberPickerQuery('');
                    }}
                    onClose={() => setDetailAudiencePickerOpen(false)}
                    onSelectMember={(id) => {
                      void persistTicketAudienceMembers(selectedTicket, [
                        ...(selectedTicket.audienceLinks ?? []).map((l) => String(l.audienceMemberId)),
                        id,
                      ]);
                      setMemberPickerQuery('');
                    }}
                    containerRef={detailAudiencePickerRef}
                    collapsedLabel="Click to search and add members…"
                    compact
                  />
                </div>

                {/* Due Date */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Due date</h3>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={dueDateToDateInputValue(selectedTicket.dueDate)}
                      onChange={async (e) => {
                        const picked = e.target.value;
                        const newDueDate = picked ? `${picked}T12:00:00.000Z` : undefined;

                        // Update local state immediately
                        setSelectedTicket({
                          ...selectedTicket,
                          dueDate: newDueDate,
                        });

                        try {
                          await updateTicketMutation.mutateAsync({
                            ticketId: selectedTicket.ticketId,
                            data: picked ? { dueDate: newDueDate } : { clearDueDate: true },
                          });
                        } catch (error) {
                          console.error('Failed to update due date:', error);
                          // Revert on error
                          setSelectedTicket({
                            ...selectedTicket,
                            dueDate: selectedTicket.dueDate,
                          });
                        }
                      }}
                      className="px-2 py-1 text-xs border border-custom-mediumSilver rounded-lg focus:outline-none"
                    />
                    {selectedTicket.dueDate && (
                      <Button
                        variant="transparent"
                        size="x-small"
                        onClick={async () => {
                          // Clear due date
                          setSelectedTicket({
                            ...selectedTicket,
                            dueDate: undefined,
                          });

                          try {
                            await updateTicketMutation.mutateAsync({
                              ticketId: selectedTicket.ticketId,
                              data: {
                                clearDueDate: true,
                              },
                            });
                          } catch (error) {
                            console.error('Failed to clear due date:', error);
                            // Revert on error
                            setSelectedTicket({
                              ...selectedTicket,
                              dueDate: selectedTicket.dueDate,
                            });
                          }
                        }}
                        title="Clear due date"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Assignee */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Assignee</h3>
                  <div className="flex items-center gap-3 ">
                    {(() => {
                      const assignee = selectedTicket.assigneeId
                        ? findTeamMemberByAssigneeId(teamMembers, selectedTicket.assigneeId)
                        : null;
                      return assignee ? (
                        <React.Fragment>
                          <Avatar
                            initials={getDisplayName(assignee)}
                            size="small"
                            imageUrl={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                              getDisplayName(assignee),
                            )}&background=6B7280&color=fff`}
                          />
                          <div className="flex-1">
                            <span className="text-sm text-gray-900">{getDisplayName(assignee)}</span>
                            <p className="text-xs text-gray-500">{assignee.email}</p>
                          </div>
                          <div className="flex items-center gap-2 ">
                            <button type="button" onClick={openAssigneeSelector} className="group">
                              <span className="group-hover:[&>*_path]:stroke-custom-blue">
                                <Pencil color={brandColors.darkSilver} size={16} />
                              </span>
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                updateTicketAssignee(selectedTicket.ticketId, undefined);
                              }}
                              className="group"
                            >
                              <span className="group-hover:[&>*_path]:stroke-custom-red">
                                <TrashIcon color={brandColors.darkSilver} size={16} />
                              </span>
                            </button>
                          </div>
                        </React.Fragment>
                      ) : (
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-xs text-custom-darkSilver">Unassigned</span>
                          <Button variant="primary" size="x-small" onClick={openAssigneeSelector}>
                            Assign
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Tags */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Tags</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Custom Tags */}
                    {selectedTicket.tags &&
                      selectedTicket.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`px-2 py-1 rounded text-xs font-medium border flex items-center gap-1 ${getTagColor(
                            tag,
                          )}`}
                        >
                          {tag}
                          <button
                            onClick={() => handleRemoveLabel(tag)}
                            className="hover:opacity-70 transition-opacity ml-1"
                          >
                            ✕
                          </button>
                        </span>
                      ))}

                    {/* Add Tag Button or Input */}
                    {!showLabelInput ? (
                      <Button variant="transparent" size="x-small" onClick={() => setShowLabelInput(true)}>
                        +
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newLabel}
                          onChange={(e) => setNewLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAddLabel();
                            } else if (e.key === 'Escape') {
                              setNewLabel('');
                              setShowLabelInput(false);
                            }
                          }}
                          placeholder="Tag name"
                          className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                        <Button variant="primary" size="x-small" onClick={handleAddLabel} disabled={isAddingLabel}>
                          {isAddingLabel ? 'Adding...' : 'Add'}
                        </Button>
                        <Button
                          variant="transparent"
                          size="x-small"
                          onClick={() => {
                            setNewLabel('');
                            setShowLabelInput(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Votes */}
                {/* <div className="mb-6">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Votes</h3>
                  <button
                    onClick={() => {
                      const userVote = getUserVote(selectedTicket);
                      handleVote(userVote === 'upvote' ? 'remove' : 'upvote');
                    }}
                    className={`px-3 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${
                      getUserVote(selectedTicket) === 'upvote'
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>👍</span>
                    {getUserVote(selectedTicket) === 'upvote' ? 'Voted' : 'Vote'}
                    <span className="ml-1 font-semibold">({selectedTicket.upvotes?.length || 0})</span>
                  </button>
                </div> */}

                {/* Status */}
                {/* <div className="mb-6">
                  <h3 className="text-sm font-medium text-custom-black mb-2">Status</h3>
                  <div className="flex flex-wrap gap-2">
                    {(['open', 'in_progress', 'resolved', 'closed'] as const).map((status) => {
                      const isActive = selectedTicket.status === status;
                      const statusLabels = {
                        open: 'Open',
                        in_progress: 'In Progress',
                        resolved: 'Resolved',
                        closed: 'Closed',
                      };
                      const statusColors = {
                        open: 'bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-300',
                        in_progress: 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300',
                        resolved: 'bg-green-100 text-green-800 hover:bg-green-200 border-green-300',
                        closed: 'bg-red-100 text-red-800 hover:bg-red-200 border-red-300',
                      };

                      return (
                        <button
                          key={status}
                          onClick={async () => {
                            if (isActive) return; // Don't update if already active

                            const previousStatus = selectedTicket.status;

                            // Update local state immediately
                            setSelectedTicket({
                              ...selectedTicket,
                              status,
                            });

                            try {
                              await updateTicketMutation.mutateAsync({
                                ticketId: selectedTicket.ticketId,
                                data: { status },
                              });
                            } catch (error) {
                              console.error('Failed to update status:', error);
                              // Revert on error
                              setSelectedTicket({
                                ...selectedTicket,
                                status: previousStatus,
                              });
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                            isActive
                              ? statusColors[status] + ' ring-2 ring-offset-1 ring-blue-500'
                              : statusColors[status] + ' opacity-60'
                          }`}
                        >
                          {statusLabels[status]}
                        </button>
                      );
                    })}
                  </div>
                </div> */}
              </div>

              {/* Right Panel - Comments and Activity */}
              <div className="flex-1 min-w-0 p-6 overflow-y-auto overflow-x-hidden">
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-custom-black">Comments and activity</h3>
                </div>

                {/* Activity log */}
                <div className="mb-6 border-b border-gray-100 pb-4">
                  <h4 className="text-xs font-semibold text-custom-darkSilver uppercase tracking-wide mb-2">Activity</h4>
                  {activityLoading ? (
                    <p className="text-xs text-gray-500">Loading…</p>
                  ) : activityRows.length === 0 ? (
                    <p className="text-xs text-gray-500">No activity yet.</p>
                  ) : (
                    <ul className="space-y-2 max-h-40 overflow-y-auto">
                      {activityRows.map((row) => (
                        <li key={row.id} className="text-xs text-custom-black">
                          <span className="text-custom-darkSilver">{new Date(row.createdAt).toLocaleString()}</span>
                          {' · '}
                          <span className="font-medium">{formatTicketActivityActor(row)}</span>
                          {' — '}
                          {row.summary}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Comment Input */}
                <div className="mb-6">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                          user?.attributes?.given_name && user?.attributes?.family_name
                            ? `${user.attributes.given_name} ${user.attributes.family_name}`
                            : user?.attributes?.email || 'User',
                        )}&background=6B7280&color=fff&size=32`}
                        alt="Your avatar"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Write a comment..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        rows={3}
                      />
                      <div className="flex justify-end mt-2">
                        <Button
                          variant="primary"
                          size="x-small"
                          onClick={handleAddComment}
                          disabled={!newComment.trim() || addCommentMutation.isLoading}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center gap-2 min-w-[100px]"
                        >
                          {addCommentMutation.isLoading ? (
                            <>
                              <LoaderCircle className="animate-spin h-4 w-4" />
                              <span>Adding...</span>
                            </>
                          ) : (
                            'Comment'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comments */}
                <div className="space-y-4">
                  {commentsLoading ? (
                    <div className="text-center py-8">
                      <LoaderCircle className="animate-spin h-6 w-6 mx-auto text-blue-600" />
                      <p className="text-gray-500 text-sm mt-2">Loading comments...</p>
                    </div>
                  ) : commentsData && commentsData.length > 0 ? (
                    commentsData
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((comment) => (
                        <div key={comment.commentId} className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                            <img
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                                comment.createdByName,
                              )}&background=6B7280&color=fff&size=32`}
                              alt={comment.createdByName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="bg-custom-silver rounded-lg p-3">
                              <p className="text-sm text-custom-black whitespace-pre-wrap">{comment.content}</p>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-medium text-custom-black">{comment.createdByName}</span>
                              <span className="text-xs text-custom-darkSilver">
                                {new Date(comment.createdAt).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">No comments yet</p>
                      <p className="text-gray-400 text-xs mt-1">Be the first to comment on this ticket</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {showAssigneeSelector &&
          selectedTicket &&
          createPortal(
            <div
              ref={assigneeOverlayRef}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
              style={{ zIndex: 10000 }}
            >
              <div className="bg-white rounded-lg shadow-xl w-96 max-w-full">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowAssigneeSelector(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      ←
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">Members</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAssigneeSelector(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4 border-b border-gray-200">
                  <input
                    type="text"
                    placeholder="Search members"
                    value={assigneeSearchTerm}
                    onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                    className="w-full min-w-0 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>
                <div className="p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Board members</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {filteredTeamMembers.map((member) => (
                      <div
                        key={assigneeOptionValue(member)}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          updateTicketAssignee(selectedTicket.ticketId, assigneeOptionValue(member));
                          setShowAssigneeSelector(false);
                          setAssigneeSearchTerm('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            updateTicketAssignee(selectedTicket.ticketId, assigneeOptionValue(member));
                            setShowAssigneeSelector(false);
                            setAssigneeSearchTerm('');
                          }
                        }}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          findTeamMemberByAssigneeId(teamMembers, selectedTicket.assigneeId)?.email === member.email
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                          <img
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                              getDisplayName(member),
                            )}&background=6B7280&color=fff&size=32`}
                            alt={getDisplayName(member)}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-gray-900 text-sm">{getDisplayName(member)}</span>
                      </div>
                    ))}
                    {filteredTeamMembers.length === 0 && (
                      <div className="text-gray-500 text-sm text-center py-4">No members found</div>
                    )}
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => {
          // Close dialog and reset mutations if not loading
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          // Reset mutations to clear any stale state
          if (!deleteTicketMutation.isLoading) {
            deleteTicketMutation.reset();
          }
          if (!deleteColumnMutation.isLoading) {
            deleteColumnMutation.reset();
          }
        }}
        isLoading={deleteTicketMutation.isLoading || deleteColumnMutation.isLoading}
      />

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />
    </div>
  );
};

export default Tickets;
